import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { Empty, Loading, Modal, TextInput, useToast } from './ui';

export interface RestaurantTable {
  id: string;
  code: string;
  seats: number;
  section: string;
  isOccupied: boolean;
  openTicket: { id: string; number: string; openedAt: string } | null;
}

/**
 * Restaurant table setup, reached from both the POS and Reservations screens.
 *
 * Like rooms, tables are added as a range: a dining room is numbered T1 to T14,
 * not created one dialog at a time.
 */
export function TableSetup({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();

  const tables = useQuery({
    queryKey: ['tables'],
    queryFn: () => api.get<RestaurantTable[]>('/api/tables'),
  });

  const [form, setForm] = useState({ prefix: 'T', from: '', to: '', seats: '4', section: 'Main' });
  const [result, setResult] = useState<string | null>(null);

  const createTables = useMutation({
    mutationFn: async () => {
      const from = Number(form.from);
      const to = Number(form.to || form.from);

      let created = 0;
      const skipped: string[] = [];
      const failed: string[] = [];

      for (let n = from; n <= to; n += 1) {
        const code = `${form.prefix.trim()}${n}`.toUpperCase();
        try {
          await api.post('/api/tables', {
            code,
            seats: Number(form.seats),
            section: form.section.trim() || 'Main',
          });
          created += 1;
        } catch (error) {
          // A code that already exists is skipped, not fatal: adding T15 to an
          // existing T1-T14 run is the normal way this gets used a second time.
          if (error instanceof ApiError && error.status === 409) skipped.push(code);
          else failed.push(code);
        }
      }
      return { created, skipped, failed };
    },
    onSuccess: ({ created, skipped, failed }) => {
      const parts = [`${created} table${created === 1 ? '' : 's'} added`];
      if (skipped.length) parts.push(`${skipped.length} already existed (${skipped.join(', ')})`);
      if (failed.length) parts.push(`${failed.length} could not be added (${failed.join(', ')})`);
      setResult(parts.join(' · '));
      if (created > 0) push(parts[0]!, 'ok');
      setForm((f) => ({ ...f, from: '', to: '' }));
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
    onError: fail,
  });

  const from = Number(form.from);
  const to = Number(form.to || form.from);
  const count = Number.isFinite(from) && Number.isFinite(to) ? to - from + 1 : 0;
  const ready = form.from !== '' && count > 0 && count <= 100 && Number(form.seats) > 0;

  const bySection = new Map<string, RestaurantTable[]>();
  for (const table of tables.data ?? []) {
    bySection.set(table.section, [...(bySection.get(table.section) ?? []), table]);
  }

  return (
    <Modal title="Set up tables" onClose={onClose} size="wide">
      <div className="stack">
        <section className="card">
          <div className="card-head">
            <h3>Tables</h3>
            <span className="chip">{tables.data?.length ?? 0} total</span>
          </div>

          {tables.isLoading ? (
            <Loading />
          ) : (tables.data?.length ?? 0) === 0 ? (
            <Empty message="No tables yet — add your dining room below" />
          ) : (
            <div className="card-body col gap-16">
              {[...bySection.entries()].map(([section, list]) => (
                <div key={section}>
                  <div className="small muted" style={{ marginBottom: 6 }}>
                    {section} · {list.length} table{list.length === 1 ? '' : 's'}
                  </div>
                  <div className="row wrap gap-6">
                    {list.map((table) => (
                      <span key={table.id} className="chip" title={`Seats ${table.seats}`}>
                        {table.code} · {table.seats}
                        {table.isOccupied ? ' · in use' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card">
          <div className="card-head">
            <h3>Add tables</h3>
            <span className="small muted">One table, or a numbered range</span>
          </div>
          <div className="card-body">
            <div className="form-grid">
              <TextInput
                label="Code prefix"
                placeholder="T"
                value={form.prefix}
                onChange={(e) => setForm({ ...form, prefix: e.target.value })}
              />
              <TextInput
                label="Section"
                placeholder="Main, Terrace, Poolside"
                value={form.section}
                onChange={(e) => setForm({ ...form, section: e.target.value })}
              />
              <TextInput
                label="From number *"
                type="number"
                placeholder="1"
                value={form.from}
                onChange={(e) => setForm({ ...form, from: e.target.value })}
              />
              <TextInput
                label="To number"
                type="number"
                placeholder="14"
                hint="Leave blank to add a single table"
                value={form.to}
                onChange={(e) => setForm({ ...form, to: e.target.value })}
              />
              <TextInput
                label="Seats per table *"
                type="number"
                min={1}
                max={30}
                value={form.seats}
                onChange={(e) => setForm({ ...form, seats: e.target.value })}
              />
            </div>

            <div className="row between wrap gap-6" style={{ marginTop: 12 }}>
              <button
                className="btn primary"
                disabled={!ready || createTables.isPending}
                onClick={() => {
                  setResult(null);
                  createTables.mutate();
                }}
              >
                {createTables.isPending
                  ? 'Adding…'
                  : count > 1
                    ? `Add ${count} tables`
                    : 'Add table'}
              </button>
              {count > 100 ? (
                <span className="small warn">That is more than 100 tables in one go.</span>
              ) : null}
            </div>

            {result ? (
              <p className="small" style={{ marginTop: 10 }}>
                {result}
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </Modal>
  );
}
